import type {
  GraphMember,
} from '@/contexts/FamilyGraphContext';

import {
  computeReachedGenerationTiers,
} from '@/lib/treeGenerationAuthority';

import {
  projectCanonicalTreeCompatibilityEdges,
} from './CanonicalTreeCompatibilityEdgeProjection';

import {
  runCanonicalTreeProjectionParityGate,
  type CanonicalTreeProjectionMigrationReadiness,
} from './CanonicalTreeProjectionParityGate';

export type CanonicalGenerationConsumerSource =
  | 'canonical'
  | 'legacy-fallback';

export interface CanonicalTreeGenerationConsumerResult {
  readonly generationCount: number;
  readonly generationTiers:
    readonly number[];

  readonly source:
    CanonicalGenerationConsumerSource;

  readonly migrationReadiness:
    CanonicalTreeProjectionMigrationReadiness;

  readonly legacyGenerationTiers:
    readonly number[];

  readonly canonicalGenerationTiers:
    readonly number[];

  readonly canonicalAdditionCount: number;
  readonly unresolvedEvidenceCount: number;
  readonly conflictingEvidenceCount: number;
}

export interface CanonicalTreeGenerationConsumerInput {
  readonly members:
    readonly GraphMember[];

  readonly viewerId:
    string | null | undefined;
}

const sortedTiers = (
  values: Iterable<number>,
): readonly number[] =>
  Object.freeze(
    Array.from(new Set(values))
      .sort((left, right) => left - right),
  );

export const resolveCanonicalTreeGenerationConsumer = (
  input: CanonicalTreeGenerationConsumerInput,
): CanonicalTreeGenerationConsumerResult => {
  const members =
    [...input.members];

  const legacyGenerationTiers =
    sortedTiers(
      computeReachedGenerationTiers(
        members,
        input.viewerId,
      ),
    );

  if (
    !input.viewerId ||
    !members.some(
      member =>
        member.id === input.viewerId,
    )
  ) {
    return Object.freeze({
      generationCount:
        legacyGenerationTiers.length,

      generationTiers:
        legacyGenerationTiers,

      source:
        'legacy-fallback',

      migrationReadiness:
        'BLOCKED',

      legacyGenerationTiers,

      canonicalGenerationTiers:
        Object.freeze([]),

      canonicalAdditionCount:
        0,

      unresolvedEvidenceCount:
        1,

      conflictingEvidenceCount:
        0,
    });
  }

  const compatibility =
    projectCanonicalTreeCompatibilityEdges({
      members,
    });

  const parity =
    runCanonicalTreeProjectionParityGate({
      viewerId:
        input.viewerId,
      members,
      edges:
        compatibility.edges,
    });

  const canonicalGenerationTiers =
    sortedTiers(
      Array.from(
        parity.canonical.projections.values(),
      )
        .map(
          projection =>
            projection.generation,
        )
        .filter(
          (
            generation,
          ): generation is number =>
            generation !== null,
        ),
    );

  const parityReady =
    parity.migrationReadiness === 'READY' ||
    parity.migrationReadiness ===
      'READY-WITH-CANONICAL-ADDITIONS';

  const compatibilityReady =
    compatibility.unresolvedEvidence.length === 0 &&
    compatibility.invalidEvidence.length === 0;

  const canonicalReady =
    parityReady &&
    compatibilityReady;

  const generationTiers =
    canonicalReady
      ? canonicalGenerationTiers
      : legacyGenerationTiers;

  return Object.freeze({
    generationCount:
      generationTiers.length,

    generationTiers,

    source:
      canonicalReady
        ? 'canonical'
        : 'legacy-fallback',

    migrationReadiness:
      canonicalReady
        ? parity.migrationReadiness
        : 'BLOCKED',

    legacyGenerationTiers,

    canonicalGenerationTiers,

    canonicalAdditionCount:
      parity.summary
        .canonicalAdditionCount,

    unresolvedEvidenceCount:
      compatibility
        .unresolvedEvidence.length +
      parity.canonical
        .unresolvedEvidence.length,

    conflictingEvidenceCount:
      parity.canonical
        .conflictingEvidence.length,
  });
};
